import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

import { setContext, G } from './ctx';
import { createEventBus } from './events';
import { createInput, type PollableInput } from './input';
import { EntityManager } from './entities';
import { buildCity } from './city';
import { Environment } from './environment';
import { Player } from './player';
import { CameraController } from './camera';
import { TrafficSystem, PedSystem } from './traffic';
import { PoliceSystem } from './police';
import { WeaponSystem } from './weapons';
import { ParticleSystem } from './particles';
import { HUD } from './hud';
import { setupTouchControls } from './touch';
import { AudioSystem } from './audio';
import { MissionSystem } from './missions';
import { resolveVehicleCollisions } from './vehicle';
import { makeRng } from './util';
import type { GameContext, GameStats } from './types';

const SAVE_KEY = 'gtb-save-v1';

function isMobileDevice(): boolean {
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) || (navigator.maxTouchPoints > 1 && window.innerWidth < 1100);
}

async function boot(): Promise<void> {
  const params = new URLSearchParams(location.search);
  const seed = Number(params.get('seed') ?? 1337);
  const isMobile = isMobileDevice();

  // ---- renderer / scene / camera ------------------------------------------------
  const canvas = document.getElementById('game') as HTMLCanvasElement;
  const ui = document.getElementById('ui') as HTMLElement;
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: !isMobile, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobile ? 1.5 : 2));
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.shadowMap.enabled = !isMobile;
  renderer.shadowMap.type = THREE.PCFShadowMap;
  renderer.info.autoReset = false; // accumulate over the whole composer frame (reset in frame())
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(62, window.innerWidth / window.innerHeight, 0.2, 1500);

  // ---- context -----------------------------------------------------------------
  const inputCtl = createInput(canvas) as PollableInput;
  const events = createEventBus();
  const rng = makeRng(seed);
  const stats: GameStats = { money: 500, kills: 0, carsStolen: 0, distanceDriven: 0, distanceWalked: 0, timePlayed: 0, deaths: 0, busts: 0, maxWanted: 0 };

  const ctx = {
    scene,
    camera,
    renderer,
    canvas,
    input: inputCtl.state,
    inputCtl,
    events,
    rng,
    time: { t: 0, dt: 0, fps: 60 },
    stats,
    controlsLocked: false,
    paused: false,
    isMobile,
    addMoney(delta: number) {
      stats.money = Math.max(0, Math.round(stats.money + delta));
      events.emit('moneyChanged', { money: stats.money, delta });
    },
  } as unknown as GameContext;
  setContext(ctx);

  // ---- world -------------------------------------------------------------------
  ctx.city = buildCity(scene, seed);
  ctx.env = new Environment(scene);
  ctx.particles = new ParticleSystem(scene);
  ctx.entities = new EntityManager();
  ctx.hud = new HUD(ui);
  ctx.audio = new AudioSystem();
  const touch = setupTouchControls(ui, inputCtl.state);
  if (touch.enabled) inputCtl.state.touch = true;

  const start = ctx.city.poi('apartment');
  ctx.player = new Player(start.pos.x, start.pos.z, start.heading);
  scene.add(ctx.player.group);
  ctx.cam = new CameraController();
  ctx.weapons = new WeaponSystem();
  ctx.police = new PoliceSystem();
  ctx.missions = new MissionSystem();
  const traffic = new TrafficSystem();
  const pedSystem = new PedSystem();

  // ---- post-processing ---------------------------------------------------------
  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));
  const bloom = new UnrealBloomPass(new THREE.Vector2(window.innerWidth, window.innerHeight), 0.32, 0.45, 0.82);
  composer.addPass(bloom);
  composer.addPass(new OutputPass());

  function applyQuality(): void {
    const s = ctx.hud.settings;
    const q = s.quality;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, q === 'low' ? 1 : q === 'medium' ? 1.5 : 2));
    renderer.shadowMap.enabled = s.shadows && q !== 'low';
    ctx.env.sun.castShadow = renderer.shadowMap.enabled;
    ctx.env.sun.shadow.mapSize.setScalar(q === 'high' ? 2048 : 1024);
    ctx.env.sun.shadow.map?.dispose();
    ctx.env.sun.shadow.map = null;
    bloom.enabled = s.bloom && q !== 'low';
    scene.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.material) {
        const mats = Array.isArray(m.material) ? m.material : [m.material];
        mats.forEach((mm) => (mm.needsUpdate = true));
      }
    });
  }
  const qParam = params.get('q');
  if (isMobile || qParam === 'low') {
    ctx.hud.settings.quality = 'low';
    ctx.hud.settings.bloom = false;
    ctx.hud.settings.shadows = false;
  } else if (qParam === 'medium' || qParam === 'high') {
    ctx.hud.settings.quality = qParam;
  }
  applyQuality();
  let lastQuality = JSON.stringify(ctx.hud.settings);

  window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
    composer.setSize(window.innerWidth, window.innerHeight);
  });

  // ---- save / load -------------------------------------------------------------
  function save(): void {
    try {
      localStorage.setItem(
        SAVE_KEY,
        JSON.stringify({ stats, weapons: ctx.weapons.serialize(), missions: ctx.missions.serialize(), dayTime: ctx.env.dayTime }),
      );
    } catch {
      /* ignore */
    }
  }
  function load(): void {
    try {
      const raw = localStorage.getItem(SAVE_KEY);
      if (!raw) return;
      const s = JSON.parse(raw);
      if (s.stats) Object.assign(stats, s.stats);
      if (s.weapons) ctx.weapons.restore(s.weapons);
      if (s.missions) ctx.missions.restore(s.missions);
      if (typeof s.dayTime === 'number') ctx.env.dayTime = s.dayTime;
    } catch {
      /* ignore */
    }
  }
  if (!params.has('fresh')) load();
  (window as unknown as { G: GameContext; saveGame: () => void }).G = ctx;
  (window as unknown as { saveGame: () => void }).saveGame = save;

  // ---- global event wiring -------------------------------------------------------
  let respawnAt = -1;
  let respawnKind: 'wasted' | 'busted' | null = null;

  events.on('playerDied', () => {
    if (respawnKind) return;
    stats.deaths++;
    respawnKind = 'wasted';
    respawnAt = ctx.time.t + 4.5;
    ctx.controlsLocked = true;
    ctx.hud.showWasted();
    ctx.audio.play('wasted');
    ctx.missions.fail('You died');
  });
  events.on('playerBusted', () => {
    if (respawnKind) return;
    stats.busts++;
    respawnKind = 'busted';
    respawnAt = ctx.time.t + 4.5;
    ctx.controlsLocked = true;
    ctx.hud.showBusted();
    ctx.audio.play('busted');
    ctx.missions.fail('You got busted');
  });
  events.on('wantedChanged', (e) => {
    if (e.level > e.previous) ctx.audio.play('star_up');
    stats.maxWanted = Math.max(stats.maxWanted, e.level);
  });
  events.on('vehicleEntered', (e) => {
    if (e.stolen) stats.carsStolen++;
    ctx.audio.play('door', e.vehicle.pos.x, e.vehicle.pos.z);
  });
  events.on('vehicleExited', (e) => ctx.audio.play('door', e.vehicle.pos.x, e.vehicle.pos.z));
  events.on('pedKilled', (e) => {
    if (e.source === 'player') stats.kills++;
  });
  events.on('vehicleDestroyed', (e) => {
    if (ctx.player.vehicle === e.vehicle) ctx.player.applyDamage(1000, 'explosion');
  });
  events.on('explosion', (e) => {
    ctx.cam.shake(Math.min(1.5, e.size * 0.6), 0.6);
    ctx.audio.play('explosion', e.x, e.z);
  });
  events.on('missionComplete', () => save());
  events.on('notify', (e) => ctx.hud.notify(e.text));

  function doRespawn(): void {
    const kind = respawnKind;
    respawnKind = null;
    ctx.hud.hideBigText();
    ctx.controlsLocked = false;
    const poi = ctx.city.poi(kind === 'busted' ? 'police' : 'hospital');
    ctx.player.respawn(poi.pos, poi.heading);
    ctx.police.wanted.clear();
    if (kind === 'busted') {
      ctx.weapons.strip();
      ctx.addMoney(-Math.round(stats.money * 0.1));
      ctx.hud.notify('Bail paid. Weapons confiscated.');
    } else {
      ctx.addMoney(-Math.round(stats.money * 0.1));
      ctx.hud.notify('Hospital bill paid.');
    }
    events.emit('playerRespawned', { x: poi.pos.x, z: poi.pos.z });
    save();
  }

  // ---- start screen -------------------------------------------------------------
  // render one frame behind the start screen so the city is visible
  ctx.cam.update(0);
  composer.render();
  await ctx.hud.showStartScreen();
  ctx.audio.unlock();
  if (!isMobile) inputCtl.requestPointerLock();
  canvas.addEventListener('click', () => {
    if (!ctx.paused && !isMobile) inputCtl.requestPointerLock();
  });

  // ---- main loop ----------------------------------------------------------------
  let last = performance.now();
  let lightsOn = false;
  let saveTimer = 0;
  let fpsAcc = 0;
  let fpsN = 0;
  const prevPos = new THREE.Vector3().copy(ctx.player.pos);

  function frame(now: number): void {
    requestAnimationFrame(frame);
    let dt = (now - last) / 1000;
    last = now;
    if (dt > 0.05) dt = 0.05;
    if (dt <= 0) dt = 0.0001;

    renderer.info.reset();
    inputCtl.poll();
    const inp = inputCtl.state;

    if (inp.pause) {
      ctx.hud.togglePause();
      ctx.paused = ctx.hud.paused;
      if (ctx.paused) inputCtl.exitPointerLock();
      else if (!isMobile) inputCtl.requestPointerLock();
    }
    ctx.paused = ctx.hud.paused;

    const q = JSON.stringify(ctx.hud.settings);
    if (q !== lastQuality) {
      lastQuality = q;
      applyQuality();
    }

    if (ctx.paused) {
      ctx.hud.update(dt);
      composer.render();
      inputCtl.endFrame();
      return;
    }

    ctx.time.dt = dt;
    ctx.time.t += dt;
    stats.timePlayed += dt;
    fpsAcc += dt;
    fpsN++;
    if (fpsAcc >= 0.5) {
      ctx.time.fps = fpsN / fpsAcc;
      fpsAcc = 0;
      fpsN = 0;
    }

    if (inp.cameraToggle) ctx.cam.cycleMode();
    if (inp.radioNext && ctx.player.vehicle) {
      const st = ctx.audio.nextStation();
      ctx.hud.showRadio(st.name);
    }

    // --- simulation -------------------------------------------------------------
    ctx.player.update(dt);
    traffic.update(dt);
    pedSystem.update(dt);
    ctx.police.update(dt);
    ctx.missions.update(dt);

    const vehicles = ctx.entities.vehicles;
    for (let i = 0; i < vehicles.length; i++) vehicles[i].update(dt);
    resolveVehicleCollisions(vehicles);

    const peds = ctx.entities.peds;
    for (let i = 0; i < peds.length; i++) peds[i].update(dt);

    ctx.weapons.update(dt);
    ctx.particles.update(dt);

    // distance stats
    const pd = ctx.player.pos.distanceTo(prevPos);
    if (pd < 50) {
      if (ctx.player.vehicle) stats.distanceDriven += pd;
      else stats.distanceWalked += pd;
    }
    prevPos.copy(ctx.player.pos);

    // --- housekeeping -------------------------------------------------------------
    for (let i = peds.length - 1; i >= 0; i--) {
      const p = peds[i];
      if (p.state === 'dead' && ctx.time.t - p.deadSince > 25) ctx.entities.removePed(p);
    }
    for (let i = vehicles.length - 1; i >= 0; i--) {
      const v = vehicles[i];
      if (v.destroyed && v !== ctx.player.vehicle && v.pos.distanceTo(ctx.player.pos) > 120 && ctx.time.t - v.lastPlayerContact > 20) ctx.entities.removeVehicle(v);
    }
    if (respawnKind && ctx.time.t >= respawnAt) doRespawn();

    // --- environment / camera / audio / hud --------------------------------------
    ctx.env.update(dt, ctx.player.pos);
    ctx.city.setNightFactor(ctx.env.nightFactor);
    ctx.city.update(dt);
    const night = ctx.env.nightFactor > 0.45;
    if (night !== lightsOn) {
      lightsOn = night;
      for (const v of vehicles) v.setLights(night);
    }
    ctx.cam.update(dt);

    ctx.audio.setEngine(ctx.player.vehicle);
    ctx.audio.setRadioOn(!!ctx.player.vehicle && !ctx.player.vehicle.destroyed);
    ctx.audio.setSirens(ctx.police.copCars.filter((v) => v.sirenOn && !v.destroyed));
    ctx.audio.update(dt);
    ctx.hud.update(dt);

    composer.render();

    saveTimer += dt;
    if (saveTimer > 30) {
      saveTimer = 0;
      save();
    }
    inputCtl.endFrame();
  }
  requestAnimationFrame(frame);
}

boot().catch((err) => {
  console.error(err);
  const el = document.createElement('pre');
  el.style.cssText = 'position:fixed;inset:0;background:#200;color:#f88;padding:20px;z-index:9999;white-space:pre-wrap;';
  el.textContent = 'Boot error: ' + (err?.stack ?? err);
  document.body.appendChild(el);
});

export { G };
